import { Typography } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getConfigurationFee,
  getExchangeRate,
  useUpdateSendMoney,
  useUpdateStatusSendMoney,
} from "../../../api/send-money";
import { useGetAvlBalanceInternal } from "../../../api/withdrawal";
import { CustomButton } from "../../../components/button/buttonCustom";
import { ButtonOutlinedCustom } from "../../../components/button/buttonOulined";
import { ButtonTransparent } from "../../../components/button/buttonTransparent";
import { purposeOfRemittanceItemNames } from "../../../components/inputs/select/purposeOfRemittance.constants";
import ConfirmModal from "../../../components/modal/confirmModal";
import { useNotification } from "../../../components/providers/notification/useNotification";
import PaymentStatus from "../../../types/paymentStatus.type";
import { useAllMetadataCurrency } from "../../../utils/hooks/useAllMetadataCurrency";
import useResponsive from "../../../utils/hooks/useResponsive";
import { shortenAddress } from "../../../utils/list.utils";
import StatusForm from "../../history/component/form/status-form";
import TextContent from "../../history/component/form/text-content";
import { Crypto, Fiat } from "./form-transfer-props";
import { Direction } from "./form-transfer-v2";

const DirectorTransferDetailV2 = ({
  detailSendMoney,
  setActionFutureDate,
  handleEdit,
  paymentCurrency,
  handleRefetchDetailSendMoney,
}: {
  detailSendMoney: any;
  setActionFutureDate: (value: boolean) => void;
  handleEdit: () => void;
  paymentCurrency: number | undefined;
  handleRefetchDetailSendMoney?: (id: number | string) => void;
}) => {
  const { open: openNotification } = useNotification();
  const navigate = useNavigate();
  const { isTablet } = useResponsive();

  const [idCancel, setIdCancel] = useState<number | null>(null);
  const [disabledPortfolio, setDisabledPortfolio] = useState(false);
  const [showReviewFee, setShowReviewFee] = useState(false);

  const { dataAllCurrency } = useAllMetadataCurrency();

  const { data: avlBalance, refetch: refetchBalance } =
    useGetAvlBalanceInternal(paymentCurrency);

  const { mutate: cancelSendMoney, isLoading: isLoadingCancel } =
    useUpdateStatusSendMoney({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification({
            key: "update_success",
            message: "Send Money order canceled successfully!",
            type: "success",
          });
        }
        setIdCancel(null);
        const isFiat = Fiat.includes(detailSendMoney?.paymentCurrency);
        navigate(
          `/history?tab=${isFiat ? "fiat" : "crypto"}&orderId=${detailSendMoney?.id
          }&type=send-money`
        );
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "add_bank_account_failure",
            message: error?.message || "Something went wrong!",
            type: "error",
          });
        }
      },
    });

  const { mutate: updateSendMoney, isLoading: isLoadingUpdate } =
    useUpdateStatusSendMoney({
      onSuccess: () => {
        const isFiat = Fiat.includes(detailSendMoney?.paymentCurrency);
        navigate(
          `/history?tab=${isFiat ? "fiat" : "crypto"}&orderId=${detailSendMoney?.id
          }&type=send-money`
        );
      },
      onError: (error: { message: string; reason_code?: string }) => {
        if (error?.reason_code === "ERR00104") {
          setShowReviewFee(true);
          return;
        }
        if (openNotification) {
          openNotification({
            key: "add_bank_account_failure",
            message: error?.message || "Something went wrong!",
            type: "error",
          });
        }
      },
    });

  const { mutate: updateOrderWithLatestFee, isLoading: isLoadingUpdateOrder } =
    useUpdateSendMoney({
      onSuccess: () => {
        setShowReviewFee(false);
        // Refetch latest order detail so director-transfer shows updated fees/amounts
        if (detailSendMoney?.id && handleRefetchDetailSendMoney) {
          handleRefetchDetailSendMoney(detailSendMoney.id);
        }
        if (openNotification) {
          openNotification({
            key: "update_fee_success",
            message:
              "Order has been updated with the latest fees. Please review and confirm again.",
            type: "success",
          });
        }
      },
      onError: (error: { message: string }) => {
        setShowReviewFee(false);
        if (openNotification) {
          openNotification({
            key: "update_fee_failed",
            message: error?.message || "Something went wrong!",
            type: "error",
          });
        }
      },
    });

  const handlePaid = () => {
    updateSendMoney({
      id: detailSendMoney?.id,
      isConfirmed: true,
    });
  };

  const handlePayLater = () => {
    setActionFutureDate(true);
  };

  const handleSubmitCancel = () => {
    if (isLoadingCancel) return;
    cancelSendMoney({
      id: Number(detailSendMoney?.id),
      status: "CANCELLED",
    });
  };

  const handleCancel = () => {
    if (isLoadingCancel) return;
    setIdCancel(null);
  };

  const reasonStatus = () => {
    switch (detailSendMoney.status?.current) {
      case PaymentStatus.PENDING:
        // return "To finalize your order, please complete the payment as instructed.";
        return "";
      case PaymentStatus.REJECTED:
        return detailSendMoney.reason || "N/A";
      case PaymentStatus.POSTPONED:
        return detailSendMoney.reason || "N/A";
      case PaymentStatus.FAILED:
        return detailSendMoney.reason || "Something went wrong.";
      case PaymentStatus.APPROVED:
        return "This request was approved by the admin.";
      case PaymentStatus.CANCELLED:
        return "Send money order canceled successfully.";
      case PaymentStatus.COMPLETED:
        return "Send money has been completed. Check your portfolio for details.";
      default:
        return "";
    }
  };

  useEffect(() => {
    if (!detailSendMoney?.paymentCurrency) return
    const intervalId = setInterval(() => {
      refetchBalance()
    }, 60000)
    return () => clearInterval(intervalId)
  }, [detailSendMoney?.paymentCurrency])

  // Align with form-transfer-v2: portfolio disabled when balance is 0 or less than required debit.
  // Required = totalSendAmount (sender amount + fees when paid by sender), fallback senderAmount then amount.
  useEffect(() => {
    if (!avlBalance) return;
    const totalBalanceString = avlBalance?.data?.data?.totalBalance ? avlBalance?.data?.data?.totalBalance : 0
    const totalSendAmountString = detailSendMoney?.amounts?.totalSendAmount ? detailSendMoney?.amounts?.totalSendAmount.replace(/,/g, "") : 0
    const senderAmountString = detailSendMoney?.amounts?.senderAmount ? detailSendMoney?.amounts?.senderAmount.replace(/,/g, "") : 0
    const totalBalance = Number(totalBalanceString);
    const totalSendAmount = Number(totalSendAmountString);
    const senderAmount = Number(senderAmountString);
    const requiredAmount = totalSendAmount > 0 ? totalSendAmount : (senderAmount > 0 ? senderAmount : Number(detailSendMoney?.amount ?? 0) || 0);

    setDisabledPortfolio(totalBalance === 0 || totalBalance < requiredAmount);
  }, [
    avlBalance,
    detailSendMoney?.amounts?.senderAmount,
    detailSendMoney?.amounts?.totalSendAmount,
    detailSendMoney?.amount,
  ]);

  const handleContinueReview = async () => {
    if (!detailSendMoney?.id) return;

    try {
      const paymentCurrencyId = paymentCurrency;
      const detail: any = detailSendMoney;

      const paymentCurrencyMeta = dataAllCurrency.find(
        (item: any) => item.id === paymentCurrencyId
      ) as any;
      const paymentDecimal = paymentCurrencyMeta?.decimals ?? 2;

      const receiveCurrencyMeta = dataAllCurrency.find(
        (item: any) => item.symbol === detail?.receiveCurrency
      ) as any;
      const receiveCurrencyId = receiveCurrencyMeta?.id;

      let latestRate = detail?.amounts?.exchangeRate || 1;
      try {
        const rateRes = await getExchangeRate({
          from: paymentCurrencyId,
          to: receiveCurrencyId,
        });
        latestRate = rateRes?.data?.data ?? latestRate;
      } catch {
        // keep previous rate if latest cannot be fetched
      }

      const feeRes = await getConfigurationFee(receiveCurrencyId);
      const feeData = feeRes?.data?.data;

      const rawReceiverAmount = Number(
        detail?.amounts?.receiverAmount ??
        detail?.amounts?.totalReceiveAmount ??
        0
      );

      const rawSenderAmount =
        latestRate && latestRate !== 0
          ? rawReceiverAmount / latestRate
          : rawReceiverAmount;

      const recalculatedSenderAmount = Number(
        rawSenderAmount.toFixed(paymentDecimal)
      );

      let feeConfigId = feeData?.id;
      const isCryptoReceive = dataAllCurrency.some(
        (item: any) => item.id === receiveCurrencyId && item.type === "CRYPTO"
      );

      const transferFeeType = isCryptoReceive
        ? feeData?.cryptoTransferFeeType
        : feeData?.fiatTransferFeeType;

      if (transferFeeType === "DYNAMIC") {
        const networkIdForFee =
          detail?.beneficiary?.networkId ??
          detail?.beneficiary?.network?.id ??
          detail?.provider?.networkId;
        if (networkIdForFee && Array.isArray(feeData?.transferFees)) {
          const matchedTransferFee = feeData.transferFees.find(
            (item: any) => item.networkId === networkIdForFee
          );
          if (matchedTransferFee?.id) {
            feeConfigId = matchedTransferFee.id;
          }
        }
      }

      const body: any = {
        id: Number(detail.id),
        senderAmount: recalculatedSenderAmount,
        receiverAmount: rawReceiverAmount,
        direction: detail?.direction ?? Direction.BEN_TO_PAY,
        receiveCurrencyId: receiveCurrencyId
          ? Number(receiveCurrencyId)
          : undefined,
        paymentCurrencyId: paymentCurrencyId
          ? Number(paymentCurrencyId)
          : undefined,
        feeConfigVersion: feeData?.version,
        exchangeRate: latestRate,
        usdToFeeCurrencyRate: feeData?.usdToFeeCurrencyRate,
        feeConfigId,
        dateOfTransaction: detail?.timestamps?.dateOfTransaction,
        purposeOfRemittance: detail?.purposeOfRemittance,
      };

      body.companyId =
        detail?.companyId ?? detail?.beneficiary?.id ?? undefined;
      body.bankAccountId =
        detail?.bankAccountId ??
        detail?.beneficiary?.bankAccountId ??
        undefined;

      body.providerWalletId =
        detail?.providerWalletId ?? detail?.provider?.walletId ?? undefined;
      body.providerBankAccountId =
        detail?.providerBankAccountId ??
        detail?.provider?.bankAccountId ??
        undefined;
      body.beneficiaryWalletId =
        detail?.beneficiaryWalletId ??
        detail?.beneficiary?.walletId ??
        undefined;

      body.feesPayer = detail?.feePayer ?? "SENDER";
      body.senderMethod = detail?.senderMethod ?? "INTERNAL";
      body.receiverMethod =
        detail?.receiverMethod ?? (detail?.receiveUser ? "INTERNAL" : "EXTERNAL");

      body.serviceProviderId =
        detail?.serviceProviderId ?? detail?.provider?.id ?? undefined;
      body.receiverUserId =
        detail?.receiverUserId ?? detail?.receiveUser?.id ?? undefined;

      body.specialInstructions = detail?.specialInstructions ?? undefined;
      body.specialInstructionsDetail = detail?.specialInstructionsDetail ?? "";
      body.documents = Array.isArray(detail?.documents)
        ? detail.documents
        : [];

      updateOrderWithLatestFee(body);
    } catch (error: any) {
      if (openNotification) {
        openNotification({
          key: "update_fee_failed",
          message: error?.message || "Something went wrong!",
          type: "error",
        });
      }
    }
  };

  return (
    <>
      <div>
        <div className="form-history">
          <StatusForm
            title={`Send Money ${detailSendMoney?.orderId}`}
            status={detailSendMoney?.status?.current}
            content={reasonStatus()}
          />
          <div className="modal-balance">
            <div className="value-balance">
              <Typography style={{ fontSize: 16, color: "white" }}>
                Total to pay
              </Typography>
              <Typography.Text className="text-gradient">
                {detailSendMoney?.amounts?.totalSendAmount}{" "}
                <Typography.Text
                  className="text-gradient"
                  style={{
                    fontSize: isTablet ? "20px" : "24px",
                  }}
                >
                  {detailSendMoney?.paymentCurrency}
                </Typography.Text>
              </Typography.Text>
            </div>
          </div>
          <div className="form-history-content">
            <TextContent
              label="Beneficiary Will Receive"
              value={`${detailSendMoney?.amounts?.totalReceiveAmount} ${detailSendMoney?.receiveCurrency}`}
              color="rgba(242, 138, 40, 1)"
            />
          </div>
          <div className="form-history-content border-top">
            <TextContent label="Payment Method" value="Portfolio" />
            <TextContent label="Currency" value={detailSendMoney?.paymentCurrency} />
            <TextContent
              label="Reference Number"
              value={detailSendMoney?.referenceNumber}
              canCopy
              color="rgba(242, 138, 40, 1)"
            />
          </div>
          <div className="form-history-content border-top">
            {detailSendMoney?.receiveUser ? (
              <>
                <TextContent
                  label="Transfer method"
                  value="SMART UID"
                  color="rgba(242, 138, 40, 1)"
                />
                <TextContent
                  label="Beneficiary"
                  value={detailSendMoney?.receiveUser?.smartUID}
                  color="rgba(242, 138, 40, 1)"
                />
              </>
            ) : (
              <>
                <TextContent
                  label="Beneficiary"
                  value={detailSendMoney?.beneficiary?.companyName}
                  color="rgba(242, 138, 40, 1)"
                />
                {Fiat.includes(detailSendMoney?.receiveCurrency) ? (
                  <>
                    <TextContent
                      label="Bank Name"
                      value={detailSendMoney?.beneficiary?.bankName}
                      canCopy
                    />
                    <TextContent
                      label="Bank SWIFT"
                      value={detailSendMoney?.beneficiary?.bankSwift}
                      canCopy
                    />
                    <TextContent
                      label="Bank Address"
                      value={detailSendMoney?.beneficiary?.bankAddress}
                      canCopy
                    />
                    <TextContent
                      label="Bank Account"
                      value={detailSendMoney?.beneficiary?.bankAccount ?? detailSendMoney?.beneficiary?.bankAccountNumber}
                      canCopy
                    />
                  </>
                ) : (
                  <>
                    <TextContent
                      label="Network"
                      value={detailSendMoney?.beneficiary?.networkName}
                    />
                    <TextContent
                      label="Beneficiary Wallet Address"
                      value={shortenAddress(
                        detailSendMoney?.beneficiary?.walletAddress
                      )}
                      realValue={detailSendMoney?.beneficiary?.walletAddress}
                      canCopy
                    />
                  </>
                )}
              </>
            )}
            <TextContent
              label="Send Date"
              value={moment(
                detailSendMoney?.timestamps?.dateOfTransaction
              ).format("DD/MM/YYYY")}
            />
            <TextContent
              label="Purpose of Remittance"
              value={
                detailSendMoney?.purposeOfRemittance &&
                purposeOfRemittanceItemNames[
                detailSendMoney.purposeOfRemittance as keyof typeof purposeOfRemittanceItemNames
                ]
              }
            />
            {detailSendMoney?.futurePaymentDate ? (
              <TextContent
                label="Future date"
                value={moment(detailSendMoney?.futurePaymentDate).format("DD/MM/YYYY")}
              />
            ) : (
              <></>
            )}
          </div>
          <div className="form-history-content border-top">
            <TextContent
              label="Provider"
              value={detailSendMoney?.provider?.provider ?? detailSendMoney?.remitter?.provider}
              color="rgba(242, 138, 40, 1)"
            />
            {Crypto.includes(detailSendMoney?.paymentCurrency) ? (
              <>
                <TextContent
                  label="Provider Wallet Network"
                  value={detailSendMoney?.providerNetwork ?? detailSendMoney?.provider?.networkName ?? detailSendMoney?.remitter?.network}
                  canCopy
                />
                <TextContent
                  label="Provider Wallet Address"
                  value={shortenAddress(detailSendMoney?.providerWalletAddress ?? detailSendMoney?.provider?.walletAddress ?? detailSendMoney?.remitter?.walletAddress)}
                  realValue={detailSendMoney?.providerWalletAddress ?? detailSendMoney?.provider?.walletAddress ?? detailSendMoney?.remitter?.walletAddress}
                  canCopy
                />
              </>
            ) : (
              <>
                <TextContent
                  label="Provider Bank Name"
                  value={detailSendMoney?.providerBankName ?? detailSendMoney?.provider?.bankName ?? detailSendMoney?.remitter?.providerBankName}
                  canCopy
                />
                <TextContent
                  label="Provider Bank SWIFT"
                  value={detailSendMoney?.providerBankSwift ?? detailSendMoney?.provider?.bankSwift ?? detailSendMoney?.remitter?.providerBankSwift}
                  canCopy
                />
                <TextContent
                  label="Provider Bank Address"
                  value={detailSendMoney?.providerBankAddress ?? detailSendMoney?.provider?.bankAddress ?? detailSendMoney?.remitter?.providerBankAddress}
                  canCopy
                />
                <TextContent
                  label="Provider Bank Account"
                  value={detailSendMoney?.providerBankAccountNumber ?? detailSendMoney?.provider?.bankAccountNumber ?? detailSendMoney?.remitter?.providerBankAccount}
                  canCopy
                />
              </>
            )}
          </div>

          <div className="form-history-content border-top">
            <TextContent
              label="Order Time"
              value={moment(detailSendMoney?.timestamps?.orderTime).format(
                "HH:mm:ss DD/MM/YYYY"
              )}
            />
          </div>
        </div>
        {detailSendMoney?.status?.current === PaymentStatus.COMPLETED ? null : (
          <div
            className="form-history-button"
            style={{ paddingBottom: "10px", gap: 0 }}
          >
            {disabledPortfolio && (
              <Typography
                style={{
                  fontSize: isTablet ? 10 : 12,
                  fontWeight: 400,
                  color: '#EC3333',
                  marginBottom: 12,
                }}>
                Your payment currency balance for this request is insufficient. Top up the balance.
              </Typography>
            )}
            <div style={{ display: 'flex', gap: 24 }}>
              <ButtonOutlinedCustom
                size="large"
                text="Edit"
                width={"100%"}
                onClick={handleEdit}
                isLoading={isLoadingUpdate}
              />
              <CustomButton
                size="large"
                text="Future date"
                width={"100%"}
                onClick={() => {
                  if (!isLoadingUpdate) handlePayLater();
                }}
                isLoading={isLoadingUpdate}
              />
              <CustomButton
                size="large"
                text="Confirm"
                width={"100%"}
                onClick={handlePaid}
                isLoading={isLoadingUpdate}
                disabled={disabledPortfolio}
              />
            </div>
            <ButtonTransparent
              width={"100%"}
              size={"large"}
              onClick={() => {
                setIdCancel(Number(detailSendMoney?.id));
              }}
              text={"Cancel Order"}
              textClassName="text-button-dangerous"
              disabled={isLoadingUpdate}
            />
          </div>
        )}
      </div>
      <ConfirmModal
        type="warning"
        title="Cancel send money?"
        description="Are you sure you want to cancel this send money order? This action cannot be undone."
        open={!!idCancel}
        onCancel={handleCancel}
        onSubmit={handleSubmitCancel}
        isLoading={isLoadingCancel}
      />
      <ConfirmModal
        type="warning"
        title="Please Confirm Changes"
        description="Fee or beneficiary receive has been updated. Take a moment to review before continuing."
        open={showReviewFee}
        onCancel={() => null}
        onSubmit={handleContinueReview}
        isLoading={isLoadingUpdateOrder}
        textSubmit="OK"
        showButtonCancel={false}
      />
    </>
  );
};

export default DirectorTransferDetailV2;
